import "./App.css";
import React, { useEffect, useState } from "react";
import { FaApple } from "react-icons/fa";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import {
  ApplyProfileBase,
  ApplyProfileRestrict,
  RemoveProfileBase,
  RemoveProfileRestric,
  ApplyModeLost,
  RemoveModeLost,
} from "./controllers/command-controller";
import { getStructuredDeviceList } from "./utils/deviceManager";
import PrimarySearchAppBar from "./components/appBar";
import StackDevices from "./components/stackDevices";
import useDeviceStore from "./store/deviceStore";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// Hook to fetch devices only once
const useDevicesOnce = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const setSelectedDevice = useDeviceStore((state) => state.setSelectedDevice);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const result = await getStructuredDeviceList();
        console.log(`✅ Dispositivos obtenidos:`, result);
        setDevices(result);
        setSelectedDevice(result[0]);
      } catch (err) {
        console.error(err);
        setError("Error cargando dispositivos");
      } finally {
        setLoading(false);
      }
    };

    fetchDevices();
  }, []);

  return { devices, loading, error };
};

function App() {
  const label = { inputProps: { "aria-label": "Switch demo" } };
  const { devices, loading, error } = useDevicesOnce();
  const selectedDevice = useDeviceStore((state) => state.selectedDevice);
  const [isLocked, setIsLocked] = useState(false);
  const searchDevice = useDeviceStore((state) => state.searchDevice);
  const [open, setOpen] = React.useState(false);
  const [messageSnackbar, setMessageSnackbar] = useState("");

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  async function sendProfileBase() {
    if (selectedDevice) {
      await ApplyProfileBase(selectedDevice.uuid);
      setMessageSnackbar("Se ha enviado el perfil base al dispositivo");
      setOpen(true);
    }
  }

  async function sendProfileLock() {
    if (selectedDevice) {
      await RemoveProfileBase(selectedDevice.uuid);
      await ApplyProfileRestrict(selectedDevice.uuid);
      setIsLocked(true);
      setMessageSnackbar("Se ha bloqueado el dispositivo");
      setOpen(true);
    }
  }

  async function sendProfileUnlock() {
    if (selectedDevice) {
      await RemoveProfileRestric(selectedDevice.uuid);
      await ApplyProfileBase(selectedDevice.uuid);
      setIsLocked(false);
      setMessageSnackbar("Se ha desbloqueado el dispositivo");
      setOpen(true);
    }
  }

  async function sendApplyModeLost() {
    if (selectedDevice) {
      await ApplyModeLost(selectedDevice.uuid);
      setMessageSnackbar("Se ha enviado el modo perdido al dispositivo");
      setOpen(true);
    }
  }
  async function sendRemoveModeLost() {
    if (selectedDevice) {
      await RemoveModeLost(selectedDevice.uuid);
      setMessageSnackbar("Se ha removido el modo perdido del dispositivo");
      setOpen(true);
    }
  }

  const filtredDevices = devices.filter((device) =>
    device.serial_number.toLowerCase().includes(searchDevice.toLowerCase())
  );

  return (
    <div className="App">
      <div className="w-full h-screen flex flex-col">
        <div className="w-full h-1/12 bg-red-500">
          <PrimarySearchAppBar />
        </div>
        <div className="w-full h-11/12 flex">
          <section className="w-[12%] bg-[#f6f6f6] h-[95%] flex flex-col items-center justify-between">
            <div className="w-full h-1/2 flex flex-col items-center">
              <ul className="w-3/4 h-[90%] py-4 flex flex-col gap-4 text-md">
                <li className="py-1 font-sans hover:bg-slate-400 cursor-pointer rounded-md">
                  Dashboard
                </li>
                <li className="py-1 font-sans hover:bg-slate-400 cursor-pointer rounded-md">
                  Locations
                </li>
                <li className="py-1 font-sans hover:bg-slate-400 cursor-pointer rounded-md">
                  Devices Groups
                </li>
                <li className="cursor-pointer rounded-md bg-slate-400 py-1 font-sans">
                  Devices
                </li>
              </ul>
            </div>
            <a
              href="https://mdm.cristopherdev.com/mdm/enroll"
              className="w-[90%] py-2 mb-[20%] cursor-pointer bg-blue-500 text-white text-center rounded-md font-sans"
            >
              Enroller
            </a>
          </section>

          <section className="border-r border-slate-300 h-screen w-1/5 flex flex-col">
            {loading && <p>⏳ Cargando dispositivos...</p>}
            {error && <p className="text-red-500">❌ {error}</p>}
            {!loading && !error && (
              <div className="w-full h-full flex flex-col">
                <h2 className="font-sans text-2xl self-start m-2">
                  Your Devices
                </h2>
                <div className="flex w-full h-3/4 justify-center">
                  <StackDevices data={filtredDevices} />
                </div>
              </div>
            )}
          </section>

          <section className="w-[63%]">
            <div className="w-3/4 m-auto h-[10%] flex items-center">
              {selectedDevice && (
                <img
                  className="h-3/4"
                  src={selectedDevice.img}
                  alt={selectedDevice.model}
                />
              )}
            </div>

            <div className="m-auto flex p-2 flex-col w-3/4 items-start">
              <h2 className="text-lg font-sans font-semibold">Details</h2>
              <div className="grid grid-cols-3 grid-rows-2 gap-4 w-full h-1/4 m-auto p-2 font-light text-sm">
                <div className="flex flex-col items-start p-2">
                  <strong className="text-[12px] text-stone-400">
                    Device Model
                  </strong>
                  <strong>{selectedDevice?.model || "No model found"}</strong>
                </div>
                <div className="flex flex-col items-start p-2">
                  <strong className="text-[12px] text-stone-400">
                    Serial Number
                  </strong>
                  <strong>
                    {selectedDevice?.serial_number || "No serial found"}
                  </strong>
                </div>
                <div className="flex flex-col items-start p-2">
                  <strong className="text-[12px] text-stone-400">Color</strong>
                  <strong>{selectedDevice?.color || "No color found"}</strong>
                </div>
                <div className="flex flex-col items-start p-2">
                  <strong className="text-[12px] text-stone-400">UUID</strong>
                  <strong className="break-words max-w-full">
                    {selectedDevice?.uuid || "No uuid found"}
                  </strong>
                </div>
                <div className="flex flex-col items-start p-2">
                  <strong className="text-[12px] text-stone-400">
                    Storage
                  </strong>
                  <strong>
                    {selectedDevice?.storage || "No storage found"}
                  </strong>
                </div>
                <div className="flex flex-col items-start p-2">
                  <strong className="text-[12px] text-stone-400">Locked</strong>
                  <div>
                    <Switch {...label} checked={isLocked} disabled />
                  </div>
                </div>
              </div>
            </div>

            <div className="w-3/4 m-auto p-2">
              <Stack spacing={2} direction="row">
                <Button
                  className={
                    selectedDevice ? "!bg-blue-500" : "!bg-slate-500 disabled"
                  }
                  onClick={sendProfileLock}
                  variant="contained"
                >
                  Lock
                </Button>
                <Button
                  className={
                    selectedDevice ? "!bg-blue-500" : "!bg-slate-500 disabled"
                  }
                  onClick={sendProfileUnlock}
                  variant="contained"
                >
                  Unlock
                </Button>
                <Button
                  className={
                    selectedDevice ? "!bg-blue-500" : "!bg-slate-500 disabled"
                  }
                  onClick={sendProfileBase}
                  variant="contained"
                >
                  Mode Basic
                </Button>
                <Button
                  className={
                    selectedDevice ? "!bg-blue-500" : "!bg-slate-500 disabled"
                  }
                  onClick={sendApplyModeLost}
                  variant="contained"
                >
                  Apply Mode Lost
                </Button>
                <Button
                  className={
                    selectedDevice ? "!bg-blue-500" : "!bg-slate-500 disabled"
                  }
                  onClick={sendRemoveModeLost}
                  variant="contained"
                >
                  Remove Mode Lost
                </Button>
              </Stack>
            </div>
          </section>
          <Snackbar
            open={open}
            autoHideDuration={3000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={handleClose}
              severity="success"
              variant="filled"
              sx={{ width: "100%", fontSize: "1.5rem" }}
            >
              {messageSnackbar}
            </Alert>
          </Snackbar>
        </div>
      </div>
    </div>
  );
}

export default App;
